import { Animation, ArgumentScale, BarSeries, EventTracker, LineSeries } from "@devexpress/dx-react-chart";
import { ArgumentAxis, Chart, Legend, PieSeries, Title, Tooltip, ValueAxis } from "@devexpress/dx-react-chart-material-ui";
import { scaleBand } from "@devexpress/dx-chart-core";
import EditIcon from "@mui/icons-material/Edit";
import EventIcon from "@mui/icons-material/Event";
import MailIcon from "@mui/icons-material/Mail";
import ShortcutIcon from "@mui/icons-material/Shortcut";
import { CircularProgress, Container, GridLegacy as Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { type ComponentProps, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getEventById, getRegistrationsById } from "../../../api/event";
import { SiteStateContext } from "../../../contexts/SiteStateContext";
import { fixSiteState } from "../../../util/site_state";
import AdminLayout from "../AdminLayout";
import { buildCumulativeRegistrationsOverTime, formatRegistrationDateTick } from "./registrationAnalytics";

// Block type constants matching the enum
const BlockTypes = {
    NAME: 14,
    GROUP: 15,
    NOTES: 16,
    CUSTOM: 17,
    UNIT: 18,
    EMAIL: 11,
    PHONE: 12,
};

// Standard block types that don't need custom object columns
const STANDARD_BLOCK_TYPES = [BlockTypes.NAME, BlockTypes.EMAIL, BlockTypes.PHONE, BlockTypes.UNIT, BlockTypes.GROUP, BlockTypes.NOTES];

// Helper function to check if a block type is enabled in the event objects
function hasEnabledBlockType(event: EventItem | undefined, blockType: number): boolean {
    if (!event?.objects) return false;
    return event.objects.some(obj => obj.type === blockType && obj.enabled);
}

// Helper function to get enabled custom (non-standard) object columns
function getCustomObjectColumns(event: EventItem | undefined): EventObject[] {
    if (!event?.objects) return [];
    return event.objects.filter(obj => obj.enabled && !STANDARD_BLOCK_TYPES.includes(obj.type));
}

const registrationsAxisTickFormat = () => (tick: string) => formatRegistrationDateTick(tick);
const REGISTRATIONS_AXIS_LABEL_DY = "16";
const REGISTRATIONS_AXIS_LABEL_ROTATION = -45;
type RegistrationsAxisLabelProps = ComponentProps<typeof ArgumentAxis.Label>;
const RegistrationsAxisLabel = (props: RegistrationsAxisLabelProps) => <ArgumentAxis.Label {...props} dy={REGISTRATIONS_AXIS_LABEL_DY} transform={`rotate(${REGISTRATIONS_AXIS_LABEL_ROTATION} ${props.x} ${props.y})`} style={{ ...props.style, textAnchor: "end" }} />;

const AdminEventView = () => {
    const params = useParams();
    const { siteState, setSiteState } = useContext(SiteStateContext);
    const [event, setEvent] = useState<EventItem>();
    const [registrations, setRegistrations] = useState<EventRegistrationEntry[]>([]);
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);

    // Charts
    const [units, setUnits] = useState<{ label: string; val: number }[]>([]);
    const [unit_types, setUnitTypes] = useState<{ label: string; val: number }[]>([]);
    const [groups, setGroups] = useState<{ label: string; val: number }[]>([]);
    
    // New charts for registration analytics
    const [registrationsOverTime, setRegistrationsOverTime] = useState<{ date: string; count: number }[]>([]);
    const [dayOfWeekData, setDayOfWeekData] = useState<{ day: string; count: number }[]>([]);
    const [top5Days, setTop5Days] = useState<{ date: string; count: number }[]>([]);
    const [hourlyData, setHourlyData] = useState<{ hour: string; count: number }[]>([]);

    useEffect(() => {
        if (setSiteState) {
            setSiteState(
                fixSiteState({
                    title: "View - Events - Admin",
                    navHighlight: "/admin/events",
                    adminPage: true,
                    actions: [
                        {
                            icon: EventIcon,
                            text: "All Events",
                            onClick: () => {
                                navigate(`/admin/events`);
                            },
                        },
                        {
                            icon: ShortcutIcon,
                            text: "Jump to",
                            onClick: () => {
                                navigate(`/event/${event?.url}`);
                            },
                        },
                        {
                            icon: EditIcon,
                            text: "Edit",
                            onClick: () => {
                                navigate(`/admin/events/${event?.id}/edit`);
                            },
                        },
                        {
                            icon: MailIcon,
                            text: "Send Email",
                            onClick: () => {
                                navigate(`/admin/events/${event?.id}/mail`);
                            },
                        },
                    ],
                })
            );
        }
    }, [setSiteState, event]);

    // Data fetching
    useEffect(() => {
        if (!params.id) return;
        getEventById(params.id)
            .then((ev) => {
                getRegistrationsById(ev.id).then((regs) => {
                    setRegistrations(regs);
                });
                setEvent(ev);
            })
            .catch((err) => {
                console.warn(err);
            });
    }, [params]);

    // Charting
    useEffect(() => {
        if (!event) return;
        console.log(`Rebuilding charts w/ ${registrations.length} registrations`);

        // Unit Charts - check block-based system first, then fall back to legacy
        const hasUnitBlock = hasEnabledBlockType(event, BlockTypes.UNIT);
        if (hasUnitBlock || event.input_unit) {
            const unit_map = registrations.map((reg) => reg.unit);
            const unit_labels = Array.from(new Set(unit_map));

            // Unit distribution
            setUnits(
                unit_labels
                    .map((unit) => {
                        return { label: unit, val: registrations.filter((reg) => reg.unit === unit).length };
                    })
                    .sort((a, b) => a.val - b.val)
            );

            console.log(units);

            // Unit type distribution
            setUnitTypes([
                { label: "Troop", val: unit_map.filter((label) => label.includes("troop")).length },
                { label: "Crew", val: unit_map.filter((label) => label.includes("crew")).length },
                { label: "Ship", val: unit_map.filter((label) => label.includes("ship")).length },
                { label: "Pack", val: unit_map.filter((label) => label.includes("pack")).length },
            ]);
        }

        // Group Charts - check block-based system first, then fall back to legacy
        const hasGroupBlock = hasEnabledBlockType(event, BlockTypes.GROUP);
        if (hasGroupBlock || event.input_group) {
            // Age group distribution
            setGroups([
                { label: "Youth", val: registrations.filter((reg) => reg.group === "youth").length },
                { label: "Youth+", val: registrations.filter((reg) => reg.group === "ya").length },
                { label: "Adult", val: registrations.filter((reg) => reg.group === "adult").length },
            ]);
        }

        // New analytics charts
        if (registrations.length > 0) {
            // 1. Registrations over time (daily aggregation)
            const dateMap = new Map<string, number>();
            registrations.forEach((reg) => {
                const date = new Date(reg.dateTime);
                const dateStr = date.toLocaleDateString("en-US");
                dateMap.set(dateStr, (dateMap.get(dateStr) || 0) + 1);
            });

            const timeSeriesData = buildCumulativeRegistrationsOverTime(registrations);
            setRegistrationsOverTime(timeSeriesData);

            // 2. Day of week analysis
            const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
            const dayMap = new Map<string, number>();
            registrations.forEach((reg) => {
                const date = new Date(reg.dateTime);
                const dayName = dayNames[date.getDay()];
                dayMap.set(dayName, (dayMap.get(dayName) || 0) + 1);
            });
            
            const dayOfWeekChartData = dayNames.map((day) => ({
                day,
                count: dayMap.get(day) || 0,
            }));
            setDayOfWeekData(dayOfWeekChartData);

            // 3. Top 5 days with most registrations
            const top5DaysData = Array.from(dateMap.entries())
                .map(([date, count]) => ({ date, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);
            setTop5Days(top5DaysData);

            // 4. Hourly distribution
            const hourMap = new Map<number, number>();
            registrations.forEach((reg) => {
                const date = new Date(reg.dateTime);
                const hour = date.getHours();
                hourMap.set(hour, (hourMap.get(hour) || 0) + 1);
            });
            
            const hourlyChartData = Array.from({ length: 24 }, (_, i) => ({
                hour: `${i}:00`,
                count: hourMap.get(i) || 0,
            }));
            setHourlyData(hourlyChartData);
        }

        setTimeout(() => {
            setReady(true);
        }, 1000);
    }, [event, registrations]);

    const columns: GridColDef[] = [
        { field: "id", headerName: "ID", width: 200, type: "string" },
        {
            field: "dateTime",
            headerName: "Timestamp",
            width: 200,
            type: "dateTime",
            valueGetter: (_value, row) => {
                return new Date(row.dateTime);
            },
        },
        { field: "email", headerName: "Email", width: 200 },
    ];

    // Check block-based system first, then fall back to legacy
    if (hasEnabledBlockType(event, BlockTypes.NAME) || event?.input_name) {
        columns.push({ field: "name", headerName: "Name", width: 200 });
    }

    if (hasEnabledBlockType(event, BlockTypes.UNIT) || event?.input_unit) {
        columns.push({ field: "unit", headerName: "Unit", width: 200 });
    }

    if (hasEnabledBlockType(event, BlockTypes.GROUP) || event?.input_group) {
        columns.push({ field: "group", headerName: "Group", width: 200 });
    }

    if (hasEnabledBlockType(event, BlockTypes.PHONE) || event?.input_phone) {
        columns.push({ field: "phone", headerName: "Phone", width: 200 });
    }

    // Add custom object columns (blocks that aren't the standard types)
    getCustomObjectColumns(event).forEach((obj) => {
        columns.push({ field: `object_${obj.id}`, headerName: obj.title, width: 200 });
    });

    if (hasEnabledBlockType(event, BlockTypes.NOTES) || event?.input_notes) {
        columns.push({ field: "notes", headerName: "Notes", width: 300, type: "string" });
    }

    function embed_objects(regs: EventRegistrationEntry[]) {
        regs.forEach((reg) => {
            const obj = JSON.parse(reg.objects);
            event?.objects.forEach((o) => {
                // @ts-ignore
                reg[`object_${o.id}`] = obj[o.id];
            });
        });

        return regs;
    }

    return (
        <AdminLayout>
            <Container sx={{ m: 0 }} maxWidth={false}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Typography variant="h2" sx={{ mt: 2, mb: 4 }}>
                        View Event
                    </Typography>
                    {event && ready ? (
                        <>
                            <Typography variant="h3">{event.title}</Typography>
                            <Paper sx={{ p: 3, mt: 2 }}>
                                <DataGrid
                                    rows={embed_objects(registrations)}
                                    columns={columns}
                                    slots={{
                                        toolbar: GridToolbar,
                                    }}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                // Hide columns status and traderName, the other columns will remain visible
                                                id: false,
                                            },
                                        },
                                    }}
                                    density="compact"
                                />
                            </Paper>
                            {registrations.length > 0 && (
                                <Grid container spacing={2} sx={{ mt: 3 }}>
                                    <Grid item xs={12}>
                                        <Paper sx={{ p: 3 }}>
                                            <Typography variant="body1">
                                                {registrations.length} registrations for {event.title}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    {(hasEnabledBlockType(event, BlockTypes.UNIT) || event.input_unit) && (
                                        <Grid item xs={12} md={4}>
                                            <Paper>
                                                <Chart data={units}>
                                                    <Title text="Units" />
                                                    <PieSeries valueField="val" argumentField="label" />
                                                    <EventTracker />
                                                    <Tooltip
                                                        contentComponent={(props) => {
                                                            return (
                                                                <div>
                                                                    {units[props.targetItem.point].label} - {props.text}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                    <Animation />
                                                </Chart>
                                            </Paper>
                                        </Grid>
                                    )}
                                    {(hasEnabledBlockType(event, BlockTypes.UNIT) || event.input_unit) && (
                                        <Grid item xs={12} md={4}>
                                            <Paper>
                                                <Chart data={unit_types}>
                                                    <Title text="Unit Types" />
                                                    <PieSeries valueField="val" argumentField="label" />
                                                    <Legend position="bottom" />
                                                    <EventTracker />
                                                    <Tooltip
                                                        contentComponent={(props) => {
                                                            return (
                                                                <div>
                                                                    {unit_types[props.targetItem.point].label} - {props.text}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                    <Animation />
                                                </Chart>
                                            </Paper>
                                        </Grid>
                                    )}
                                    {(hasEnabledBlockType(event, BlockTypes.GROUP) || event.input_group) && (
                                        <Grid item xs={12} md={4}>
                                            <Paper>
                                                <Chart data={groups}>
                                                    <Title text="Age Groupings" />
                                                    <PieSeries valueField="val" argumentField="label" />
                                                    <Legend position="bottom" />
                                                    <EventTracker />
                                                    <Tooltip
                                                        contentComponent={(props) => {
                                                            return (
                                                                <div>
                                                                    {groups[props.targetItem.point].label} - {props.text}
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                    <Animation />
                                                </Chart>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                            {registrations.length > 0 && (
                                <>
                                    <Grid container spacing={2} sx={{ mt: 3 }}>
                                        <Grid item xs={12}>
                                            <Typography variant="h4" sx={{ mb: 2 }}>
                                                Registration Analytics
                                            </Typography>
                                        </Grid>

                                        {/* Registrations Over Time Chart */}
                                        <Grid item xs={12} md={12}>
                                            <Paper sx={{ p: 2 }}>
                                                <Chart data={registrationsOverTime} height={300}>
                                                    <Title text="Registrations Over Time" />
                                                    <ArgumentAxis tickFormat={registrationsAxisTickFormat} labelComponent={RegistrationsAxisLabel} />
                                                    <ValueAxis />
                                                    <LineSeries valueField="count" argumentField="date" />
                                                    <EventTracker />
                                                    <Tooltip />
                                                    <Animation />
                                                </Chart>
                                            </Paper>
                                        </Grid>

                                        {/* Day of Week Distribution Chart */}
                                        <Grid item xs={12} md={6}>
                                            <Paper sx={{ p: 2 }}>
                                                <Chart data={dayOfWeekData} height={300}>
                                                    <ArgumentScale factory={scaleBand} />
                                                    <Title text="Registrations by Day of Week" />
                                                    <ArgumentAxis />
                                                    <ValueAxis />
                                                    <BarSeries valueField="count" argumentField="day" />
                                                    <EventTracker />
                                                    <Tooltip />
                                                    <Animation />
                                                </Chart>
                                            </Paper>
                                        </Grid>

                                        {/* Hourly Distribution Chart */}
                                        <Grid item xs={12} md={6}>
                                            <Paper sx={{ p: 2 }}>
                                                <Chart data={hourlyData} height={300}>
                                                    <ArgumentScale factory={scaleBand} />
                                                    <Title text="Registrations by Hour of Day" />
                                                    <ArgumentAxis />
                                                    <ValueAxis />
                                                    <BarSeries valueField="count" argumentField="hour" />
                                                    <EventTracker />
                                                    <Tooltip />
                                                    <Animation />
                                                </Chart>
                                            </Paper>
                                        </Grid>

                                        {/* Top 5 Days Table */}
                                        <Grid item xs={12} md={6}>
                                            <Paper sx={{ p: 2 }}>
                                                <Typography variant="h6" sx={{ mb: 2 }}>
                                                    Top 5 Days with Most Registrations
                                                </Typography>
                                                <TableContainer>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell><strong>Date</strong></TableCell>
                                                                <TableCell align="right"><strong>Registrations</strong></TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {top5Days.map((day, index) => (
                                                                <TableRow key={index}>
                                                                    <TableCell>{day.date}</TableCell>
                                                                    <TableCell align="right">{day.count}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </>
                    ) : (
                        <CircularProgress />
                    )}
                </LocalizationProvider>
            </Container>
        </AdminLayout>
    );
};

export default AdminEventView;
